import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { Badge, Col, FormGroup, Input, Label, Row } from "reactstrap";
import { getUserListAPI } from "../../api/user";
import {
  Block,
  BlockBetween,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  Icon,
} from "../../components/Component";
import useLogout from "../../Hooks/Logout";
import Content from "../../layout/content/Content";
import Head from "../../layout/head/Head";
import ApiPaginationDataTable from "../../MyComponents/MyTables/ApiPaginationDataTable";
import MyInput from "../../MyComponents/Form/MyInput";
import { formatDateTimeIST } from "../../utils/Utils";

const SOURCE_OPTIONS = [
  { value: "all", label: "All sources" },
  { value: "mobile_app", label: "Mobile app (default)" },
  { value: "legacy", label: "Legacy (before tracking)" },
  { value: "android", label: "Android (header)" },
  { value: "ios", label: "iOS (header)" },
];

const UserOriginsReport = () => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemPerPage, setItemPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [debouncedCity, setDebouncedCity] = useState("");
  const [debouncedRegion, setDebouncedRegion] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const logout = useLogout();

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const {
    register,
    setValue,
    watch,
    trigger,
    formState: { errors },
  } = useForm({
    defaultValues: {
      limit: 25,
      server_search: "",
      filter_city: "",
      filter_region: "",
    },
  });

  const limit = watch("limit");
  const serverSearchInput = watch("server_search") ?? "";
  const filterCityInput = watch("filter_city") ?? "";
  const filterRegionInput = watch("filter_region") ?? "";

  const handlePerRowsChange = (newPerPage, page) => {
    const rows = isNaN(Number(newPerPage)) ? itemPerPage : newPerPage;
    setItemPerPage(rows);
    setValue("page", page);
  };
  const handlePageChange = (page) => setValue("page", page);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(serverSearchInput.trim()), 450);
    return () => clearTimeout(t);
  }, [serverSearchInput]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedCity(String(filterCityInput).trim()), 450);
    return () => clearTimeout(t);
  }, [filterCityInput]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedRegion(String(filterRegionInput).trim()), 450);
    return () => clearTimeout(t);
  }, [filterRegionInput]);

  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, sourceFilter]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedCity, debouncedRegion]);

  const columns = [
    { name: "Name", selector: (row) => row?.name, sortable: true, wrap: true },
    { name: "Mobile", selector: (row) => row?.phone, sortable: true },
    {
      name: "Source",
      selector: (row) => row?.registration_source || "—",
      maxWidth: "110px",
      wrap: true,
    },
    {
      name: "IP",
      selector: (row) => row?.signup_ip || "—",
      maxWidth: "120px",
      wrap: true,
    },
    { name: "Country", selector: (row) => row?.signup_country || "—", wrap: true },
    { name: "State / region", selector: (row) => row?.signup_region || "—", wrap: true },
    { name: "City", selector: (row) => row?.signup_city || "—", wrap: true },
    {
      name: "Created (IST)",
      selector: (row) => formatDateTimeIST(row?.created_at),
      sortable: true,
    },
    {
      name: "Status",
      selector: (row) => row?.status,
      cell: (row) => (
        <Badge color={row.status ? "success" : "danger"}>{row.status ? "Active" : "Inactive"}</Badge>
      ),
      wrap: true,
    },
  ];

  useEffect(() => {
    const body = {
      search: debouncedSearch,
      page: currentPage,
      limit: itemPerPage,
      ...(statusFilter === "active" ? { status: true } : {}),
      ...(statusFilter === "inactive" ? { status: false } : {}),
      ...(debouncedCity ? { city: debouncedCity } : {}),
      ...(debouncedRegion ? { region: debouncedRegion } : {}),
      ...(sourceFilter !== "all" ? { registration_source: sourceFilter } : {}),
    };
    setIsLoading(true);
    getUserListAPI(body)
      .then((res) => {
        const status = res.data.status;
        if (status === true) {
          const d = res.data.data;
          setValue("limit", d?.pagination?.total);
          setTotalRows(d?.pagination?.total ?? 0);
          setData(d?.items ?? []);
          toast.clearWaitingQueue();
        } else if (status === false) {
          toast.warning(res.data.message);
          setData([]);
          setTotalRows(0);
        } else if (status === "expired") {
          toast.error(res.data.message);
          logout();
        }
      })
      .catch(() => {
        toast.error("Failed to load report");
        setData([]);
        setTotalRows(0);
      })
      .finally(() => setIsLoading(false));
  }, [
    itemPerPage,
    currentPage,
    debouncedSearch,
    debouncedCity,
    debouncedRegion,
    statusFilter,
    sourceFilter,
    watch("page"),
    logout,
    setValue,
  ]);

  const clearReportFilters = () => {
    setValue("server_search", "");
    setValue("filter_city", "");
    setValue("filter_region", "");
    setStatusFilter("all");
    setSourceFilter("all");
    setCurrentPage(1);
  };

  const activeFilterSummary =
    statusFilter !== "all" ||
    sourceFilter !== "all" ||
    debouncedSearch ||
    debouncedCity ||
    debouncedRegion
      ? `Filters: ${statusFilter !== "all" ? `status=${statusFilter}` : "all statuses"} · ${sourceFilter !== "all" ? `source=${sourceFilter}` : "all sources"}${debouncedSearch ? ` · search “${debouncedSearch}”` : ""}${debouncedCity ? ` · city “${debouncedCity}”` : ""}${debouncedRegion ? ` · region “${debouncedRegion}”` : ""}`
      : null;

  return (
    <>
      <Head title="User origins report" />
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle page tag="h3">
                User origins (mobile app)
              </BlockTitle>
              <BlockDes className="text-soft">
                When a user completes OTP on the mobile app, the API records{" "}
                <code className="small">X-Registration-Source</code> (e.g. android, ios, mobile_app) and resolves{" "}
                <strong>country / state / city</strong> from the client IP via ip-api.com. Put your reverse proxy in
                front of the API so <code className="small">X-Forwarded-For</code> carries the real public IP.
              </BlockDes>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <Block>
          <Row className="g-gs mb-3 align-items-end">
            <Col md="4" lg="3">
              <FormGroup className="mb-0">
                <Label className="overline-title text-soft mb-1">Registration source</Label>
                <Input
                  type="select"
                  value={sourceFilter}
                  onChange={(e) => setSourceFilter(e.target.value)}
                  className="form-select"
                >
                  {SOURCE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
            <Col md="4" lg="3">
              <MyInput
                register={register}
                watch={watch}
                setValue={setValue}
                trigger={trigger}
                errors={errors}
                title="City contains"
                name="filter_city"
                placeholder="e.g. Mumbai"
              />
            </Col>
            <Col md="4" lg="3">
              <MyInput
                register={register}
                watch={watch}
                setValue={setValue}
                trigger={trigger}
                errors={errors}
                title="State / region contains"
                name="filter_region"
                placeholder="e.g. Maharashtra"
              />
            </Col>
            <Col md="12" lg="3" className="mb-0">
              <Button type="button" color="light" outline className="report-table-btn-reset" onClick={clearReportFilters}>
                <Icon name="reload" />
                <span className="ms-1">Reset filters</span>
              </Button>
            </Col>
          </Row>

          <ApiPaginationDataTable
            data={data}
            limit={limit}
            totalRows={totalRows}
            setItemPerPage={setItemPerPage}
            itemPerPage={itemPerPage}
            perPage={itemPerPage}
            paginate={paginate}
            currentPage={currentPage}
            columns={columns}
            title="Registration geography"
            subtitle="Filter by source, partial city/state, or free-text search (name, phone, IP, location fields)."
            isLoading={isLoading}
            register={register}
            trigger={trigger}
            watch={watch}
            setValue={setValue}
            errors={errors}
            serverSearch
            paginationServer
            handlePageChange={handlePageChange}
            handlePerRowsChange={handlePerRowsChange}
            expA
            expKeys={[
              "name",
              "phone",
              "registration_source",
              "signup_ip",
              "signup_country",
              "signup_region",
              "signup_city",
              "created_at",
              "status",
            ]}
            fileName="user-origins-report"
            searchPlaceholder="Search name, phone, IP, city, state, country…"
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
            showStatusFilter
            onClearFilters={clearReportFilters}
            activeFilterSummary={activeFilterSummary}
          />
        </Block>
      </Content>
    </>
  );
};

export default UserOriginsReport;
